import React from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { isValidIdV4 } from '~/helpers/uuid';
import projectConfig, { PROJECT } from '~/projects/config';

const headProject =
  projectConfig.project === PROJECT.SIDEKICKER ? 'Sidekicker' : 'JustDone';

const WHITELIST = [
  '/home',
  '/tools',
  '/chat',
  '/ai_researcher',
  '/image_generation',
  '/extension',
];

const formatSegment = (segment: string): string =>
  segment
    .split(/[_-]/)
    .map((word) =>
      word === 'ai'
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');

const getHelmetMeta = (
  pathname: string,
): { title: string; description: string } => {
  const segments = pathname.split('/').filter(Boolean);
  const isWhitelisted = WHITELIST.some((path) => pathname.startsWith(path));

  const defaultTitle = `${headProject} AI`;
  const defaultDescription = `${headProject} AI helps you create, check, and improve your content with advanced AI tools.`;

  if (!isWhitelisted || !segments.length) {
    return { title: defaultTitle, description: defaultDescription };
  }

  const last = segments[segments.length - 1].toLowerCase();
  let pageName: string;

  if (last === 'index' || isValidIdV4(last)) {
    pageName = formatSegment(segments[0]);
  } else {
    const firstFormatted = formatSegment(segments[0]);
    const lastFormatted = formatSegment(last);
    pageName =
      firstFormatted === lastFormatted ? firstFormatted : lastFormatted;
  }

  const title = `${headProject} AI${pageName ? ` - ${pageName}` : ''}`;
  const description = `Explore ${headProject} AI's ${pageName} features. Trusted by 2M+ users worldwide.`;

  return { title, description };
};

interface Props {
  children: React.ReactNode;
}

const RouteHelmet: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation();

  const { title, description } = getHelmetMeta(pathname);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      {children}
    </>
  );
};

export default RouteHelmet;
